import { CookieService } from 'ngx-unificator/services';
import { delay, distinctUntilChanged, filter, map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { PlatformService } from '../../core/services/platform.service';
import { StaticContentService } from '../../core/services/static-content.service';
import { UserService } from '../../core/services/user/user.service';
import { UserGroupsService } from '../../core/services/user/user-groups.service';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { inject } from '@angular/core';
import { ModalService } from '../../core/modal/modal.service';
import { Observable } from 'rxjs';
import { UserBonusesService } from '../../core/services/user/user-bonuses.service';
import { UserPaymentModeService } from '../../core/services/user/user-payment-mode.service';
import { StaticPageService } from '../static-page/static-page.service';
import { LootboxService } from '../../core/services/lootbox/lootbox.service';
import { TimeService } from '../../core/services/time.service';
import { TournamentsService } from '../tournaments/tournaments.service';
import { GlobalEventsService } from '../../core/services/global-events.service';
import { LocalHistoryService } from '../../core/services/local-history.service';
import { TranslationService } from '../../core/services/translation/translation.service';
import { WindowService } from '../../core/services/window.service';
import { LocalstorageService } from '../../core/services/localstorage.service';

type StringEnum = Record<string, string>;

type ActionRouteConfig = {
  key: string;
  url: string;
  authUrl?: string[];
  nativeUrl?: boolean;
}

type ActionRoutesConfig<E extends StringEnum> = {
  [K in E[keyof E]]: ActionRouteConfig;
};

export enum EventType {
  First = 'first',
  Second = 'second',
  Third = 'third'
}

export interface IBasePromo {
  HuntModal: {
    BonusGroupClaimed: string;
    BonusGroupCatch: string;
    Title: string;
    Desc: string;
    Rule: string;
    AboutPromo: string;
    CongratsTitle: string;
    Prize: string;
    FinalDesc: string;
    Code: string;
    DepositRule: string;
    Terms: string;
  };
  WelcomePromoModal: {
    Title: string;
    Desc: string;
    Prize: string;
    Rule: string;
  };
}

export abstract class BasePromo {

  /**
   * Abstract event name for hunt(halloween, easter-eggs ...)
   */
  public abstract eventName: string;

  /**
   * Event type elements
   */
  public eventType = EventType;

  /**
   * Event collected list
   */
  public eventList: unknown[] = null;

  /**
   * Event Loaded
   */
  private _eventLoaded = null;

  public huntModalData: any = {};

  private _platform: PlatformService = inject(PlatformService);
  private _static: StaticContentService = inject(StaticContentService);
  private _events: GlobalEventsService = inject(GlobalEventsService);
  public cookie: CookieService = inject(CookieService);
  public group: UserGroupsService = inject(UserGroupsService);
  public bonuses: UserBonusesService = inject(UserBonusesService);
  public user: UserService = inject(UserService);
  public paymentMode: UserPaymentModeService = inject(UserPaymentModeService);
  public page: StaticPageService = inject(StaticPageService);
  public lootboxService: LootboxService = inject(LootboxService);
  public time: TimeService = inject(TimeService);
  public tournaments: TournamentsService = inject(TournamentsService);
  public local: LocalHistoryService = inject(LocalHistoryService);
  public router: Router = inject(Router);
  public route: ActivatedRoute = inject(ActivatedRoute);
  public translate: TranslationService = inject(TranslationService);
  public window: WindowService = inject(WindowService);
  public platform: PlatformService = inject(PlatformService);
  public modals: ModalService = inject(ModalService);
  public localStorage: LocalstorageService = inject(LocalstorageService);

  constructor() {
  }

  public get eventEnable(): boolean {
    return this._eventLoaded;
  }

  public get isFirstExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.First) && this.user.auth;
  }

  public get isSecondExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.Second) && this.user.auth;
  }

  public get isThirdExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.Third) && this.user.auth;
  }

  public addEventToCollection(type: EventType) {
    if (this._platform.isBrowser) {
      this.eventList.push(type);
      this.cookie.set(this.eventName, JSON.stringify(this.eventList), 999, '/');
      if (this.eventList.length === 3) {
        this.group.addToGroup(this.huntModalData.BonusGroupCatch)?.subscribe();
      }
      this.openHuntModal(type);
    }
  }

  public initHuntEvent(HuntModal: any) {
    this.huntModalData = HuntModal;
    if (!this.group.isExistGroup(this.huntModalData?.BonusGroupClaimed)) {
      if (this.group.isExistGroup(this.huntModalData?.BonusGroupCatch)) {
        this.eventList = [this.eventType.First, this.eventType.Second, this.eventType.Third];
      } else {
        this.eventList = this.cookie.check(this.eventName) ? JSON.parse(this.cookie.get(this.eventName)) : [];
      }
      this._eventLoaded = true;
    } else {
      this._eventLoaded = false;
    }
  }

  public initWelcomePromoModal() {
    if (this._platform.isBrowser) {
      this._checkShouldOpenWelcomeModal$().subscribe();
    }
  }

  public async onAction<E extends StringEnum>(
    actionEnum: E,
    action: E[keyof E],
    configMap: ActionRoutesConfig<E>,
    bonusCode?: string,
    successUrl: string = this.window.nativeWindow.location.href + '?deposit_success=true',
  ) {
    const routeConfig = configMap[action];
    const { key, url, authUrl } = routeConfig;

    if (action === actionEnum.GO_TO_DEPOSIT) {
      await this._handleActionRoute(
        key,
        url,
        authUrl,
        { bonusCode },
        { queryParams: { successUrl, bonusCode } },
      );
    } else if (configMap[action]?.nativeUrl) {
      this.window.nativeWindow.open(configMap[action].url, '_blank');
    } else {
      await this._handleActionRoute(key, url, authUrl);
    }
  }

  private async _handleActionRoute(
    key: string,
    url: string,
    authUrl: string[],
    urlData?: Record<string, any>,
    extras?: NavigationExtras,
  ): Promise<void> {
    if (!key) {
      await this.router.navigateByUrl(url);
      return;
    }

    if (!this.user.auth) {
      this.local.addUrl(key, url, urlData);
      await this.user.authUser();
      return;
    }

    await this.router.navigate(authUrl, extras);
  }

  private _checkShouldOpenWelcomeModal$(): Observable<boolean> {
    const routesIgnore = ['403', '404', 'register', 'login', 'deposit', 'cashout', 'games', 'promo', 'play'];
    return this._events.routerNavigationEnd$
      .pipe(
        takeWhile(() => !this.cookie.check('welcome-modal-promo'), true),
        distinctUntilChanged(),
        delay(120000),
        filter(() => {
          const hasWelcomeModalCookie = this.cookie.check('welcome-modal-promo');
          const shouldOpenModal = this.user.auth && !hasWelcomeModalCookie;
          return shouldOpenModal && !routesIgnore.some(route => this.router.url.includes(route));
        }),
        switchMap(() => this.checkActivatedBonuses()),
      );
  }

  public abstract openHuntModal?(type: EventType): void;

  public abstract checkActivatedBonuses?(): Observable<any> | Promise<any>;

}
